import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _PropTypes from 'prop-types';
import * as React from 'react';
import styled from 'styled-components';
import { spacing4x, spacing2x } from '../../styles/spacing.js';
import { mergeProps } from '../../utils/react.js';
import { useAccordionContext } from '../AccordionContext/AccordionContext.js';
import { useAccordionDndContext } from '../AccordionDndContext/AccordionDndContext.js';
import { StyledDragHandle } from '../AccordionDndContext/DragAccordion.js';
import { useAccordionItemContext } from '../AccordionItemContext/AccordionItemContext.js';
import { useAccordionItemDndContext } from '../AccordionItemDndContext/AccordionItemDndContext.js';
import { IconArrowDown } from '../Icons/IconArrowDown.js';
import { IconArrowUp } from '../Icons/IconArrowUp.js';
import { IconDrag } from '../Icons/IconDrag.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';

var _excluded = ["ariaLevel", "children"];
var StyledButton = styled.button.withConfig({
  displayName: "AccordionHeader__StyledButton",
  componentId: "sc-th9hpo-0"
})(function (_ref) {
  var isDragEnabled = _ref.isDragEnabled,
      hovered = _ref.hovered,
      theme = _ref.theme;
  return {
    alignItems: "center",
    display: "flex",
    border: "2px solid transparent",
    color: theme.neutralColors.charcoal,
    justifyContent: "space-between",
    padding: spacing4x,
    width: "100%",
    background: hovered ? theme.neutralColors.pearl : "white",
    paddingLeft: isDragEnabled && hovered ? spacing2x : void 0,
    ":focus": {
      border: "2px solid ".concat(theme.alertColors.information)
    }
  };
});
var StyledHeader = styled.div.withConfig({
  displayName: "AccordionHeader__StyledHeader",
  componentId: "sc-th9hpo-1"
})(["&&{font-weight:bold;font-size:20px;line-height:28px;display:flex;flex-direction:row;}"]);

var AccordionHeader = function AccordionHeader(props) {
  var _props$ariaLevel = props.ariaLevel,
      ariaLevel = _props$ariaLevel === void 0 ? 3 : _props$ariaLevel,
      children = props.children,
      rest = _objectWithoutProperties(props, _excluded);

  var _useAccordionItemCont = useAccordionItemContext(),
      focused = _useAccordionItemCont.focused,
      headerId = _useAccordionItemCont.headerId,
      index = _useAccordionItemCont.index,
      onHeaderEvent = _useAccordionItemCont.onHeaderEvent,
      open = _useAccordionItemCont.open,
      panelId = _useAccordionItemCont.panelId,
      setHeaderContent = _useAccordionItemCont.setHeaderContent;

  var _useAccordionContext = useAccordionContext(),
      onSelect = _useAccordionContext.onSelect;

  var dndContext = useAccordionDndContext();
  var itemDndContext = useAccordionItemDndContext();

  var _React$useState = React.useState(false),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      hovered = _React$useState2[0],
      setHovered = _React$useState2[1];

  var Icon = open ? IconArrowUp : IconArrowDown;

  function handleOnClick(e) {
    e.preventDefault();
    onSelect(index);
  }

  function handleEvent(e) {
    onHeaderEvent(e, index);
  }

  var ref = React.useRef(null);
  React.useEffect(function () {
    if (focused) {
      var _ref$current;

      ref === null || ref === void 0 ? void 0 : (_ref$current = ref.current) === null || _ref$current === void 0 ? void 0 : _ref$current.focus();
    }
  }, [focused]);
  React.useEffect(function () {
    setHeaderContent(children);
  }, [children, setHeaderContent]);
  return /*#__PURE__*/jsx("div", {
    onMouseEnter: function onMouseEnter() {
      return setHovered(true);
    },
    onMouseLeave: function onMouseLeave() {
      return setHovered(false);
    },
    style: {
      flex: 1
    },
    children: /*#__PURE__*/jsxs(StyledHeader, {
      role: "heading",
      "aria-level": ariaLevel,
      children: [(dndContext === null || dndContext === void 0 ? void 0 : dndContext.onDragChange) && itemDndContext && hovered && /*#__PURE__*/jsx(StyledDragHandle, {
        disabled: !itemDndContext.canDragAccordion,
        isDragPlaceholder: itemDndContext.isDragging,
        ref: itemDndContext.connectDragSource,
        children: /*#__PURE__*/jsx(IconDrag, {
          size: 16
        })
      }), dndContext !== null && dndContext !== void 0 && dndContext.onDragChange && itemDndContext && itemDndContext !== null && itemDndContext !== void 0 && itemDndContext.isDragging ? /*#__PURE__*/jsx(Fragment, {}) : /*#__PURE__*/jsxs(StyledButton, _objectSpread(_objectSpread({
        "aria-controls": panelId,
        "aria-expanded": !!open,
        id: headerId,
        isDragEnabled: !!(dndContext !== null && dndContext !== void 0 && dndContext.onDragChange),
        hovered: hovered,
        type: "button",
        ref: ref
      }, mergeProps(rest, {
        onClick: handleOnClick,
        onFocus: handleEvent,
        onKeyDown: handleEvent
      })), {}, {
        children: [children, /*#__PURE__*/jsx(Icon, {})]
      }))]
    })
  });
};

AccordionHeader.propTypes = {
  "ariaLevel": _PropTypes.number,
  "children": _PropTypes.node
};
AccordionHeader.displayName = "AccordionHeader";

export { AccordionHeader };
